import { AddressDTO } from "../../state/types";

export interface BuildingLike {
  extId: string;
  addresses?: Array<string>;
}

export type TabKey = "addresses" | "buildings" | "perBuilding" | "perAddress";

export interface BuildingAddressJoin<B extends BuildingLike> {
  addressesPerBuilding: Map<string, AddressDTO[]>;
  buildingsPerAddress: Map<string, B[]>;
  multiAddressBuildings: boolean;
  multiBuildingAddresses: boolean;
}

export function joinBuildingsAddresses<B extends BuildingLike>(
  buildings: B[],
  addresses: AddressDTO[]
): BuildingAddressJoin<B> {
  const addressById = new Map(addresses.map((a) => [a.extId, a]));
  const addressesPerBuilding = new Map<string, AddressDTO[]>();
  const buildingsPerAddress = new Map<string, B[]>(addresses.map((a) => [a.extId, []]));
  for (const b of buildings) {
    const linked = (b.addresses ?? [])
      .map((id) => addressById.get(id))
      .filter((a): a is AddressDTO => Boolean(a));
    addressesPerBuilding.set(b.extId, linked);
    for (const a of linked) buildingsPerAddress.get(a.extId)!.push(b);
  }
  return {
    addressesPerBuilding,
    buildingsPerAddress,
    multiAddressBuildings: [...addressesPerBuilding.values()].some((l) => l.length > 1),
    multiBuildingAddresses: [...buildingsPerAddress.values()].some((l) => l.length > 1),
  };
}

// Which tabs are relevant follows the cardinality of the building-address
// relation: a grouping tab only earns its place when it groups more than 1:1.
export function relevantTabs<B extends BuildingLike>(
  buildings: B[],
  addresses: AddressDTO[],
  join: BuildingAddressJoin<B>
): TabKey[] {
  const tabs: TabKey[] = [];
  if (addresses.length) tabs.push("addresses");
  if (buildings.length) tabs.push("buildings");
  if (join.multiAddressBuildings) tabs.push("perBuilding");
  if (join.multiBuildingAddresses) tabs.push("perAddress");
  return tabs;
}
